function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import BackToApps from "../../components/back-to-apps";
import { Ribbon, Empty } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import { num } from "../sales/territory-common";
import { kwService, fmtDate, isoDay, PositionChip, Chip, RowMenu, Verdict, plural, SelfSiteCard, selfRow } from "./common";

/*
 * Keyword Intelligence dashboard (KEYWORD_INTELLIGENCE.md section 6; layout per
 * mockups/apps-redesign-2026-10-09.html board 5).
 *
 * Backend: MarketResearchService /v1/market-research/keywords/*. Reads the
 * registry (all keywords incl. paused), overview (latest position per active
 * keyword x engine), share of voice snapshots, competitor registry,
 * opportunities and cannibalization. The rank data has no per-term
 * competitor positions, so the table has no "Who ranks" column; competitor
 * standing comes from the share of voice snapshot instead.
 */

const ENGINES = ["google", "bing"];
const FILTERS = [["act", "Worth acting on"], ["all", "All"], ["ranking", "Ranking"], ["paused", "Paused"]];
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}

// Latest share of voice snapshot: one engine (google first), latest date.
function latestSov(sov) {
  const rows = sov || [];
  if (!rows.length) return {
    rows: [],
    date: null
  };
  const engines = [...new Set(rows.map(r => r.engine || ""))];
  const engine = engines.includes("google") ? "google" : engines[0];
  const scoped = rows.filter(r => (r.engine || "") === engine);
  const date = scoped.map(r => isoDay(r.checkedDate)).sort().pop();
  return {
    date,
    rows: scoped.filter(r => isoDay(r.checkedDate) === date).sort((a, b) => (Number(b.sharePct) || 0) - (Number(a.sharePct) || 0))
  };
}

// Share of voice = the share of the search clicks on your terms a site is
// estimated to get (vendor traffic estimate / total monthly searches, 0-100).
// One very broad term can hold most of the searches, so shares are often
// small: show a decimal below 1% instead of rounding everything to 0%.
function fmtShare(v) {
  if (v == null || isNaN(Number(v))) return "";
  const n = Number(v);
  if (n === 0) return "0%";
  if (n < 0.1) return "under 0.1%";
  if (n < 10) return `${(Math.round(n * 10) / 10).toLocaleString("en-US")}%`;
  return `${Math.round(n)}%`;
}
class KeywordsViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "reload", () => {
      this.setState({
        data: null,
        error: false
      });
      this.load();
    });
    // ---------------------------------------------------------- registry ops
    _defineProperty(this, "addKeywords", async () => {
      const terms = this.state.addTerms.split(/[\n,]+/).map(t => t.trim()).filter(Boolean);
      if (!terms.length || this.state.saving) return;
      const engines = ENGINES.filter(e => this.state.addEngines[e]).join(",") || "google";
      const models = terms.map(term => ({
        term,
        engines,
        topicGroup: this.state.addTopic.trim() || null,
        targetUrl: this.state.addTarget.trim() || null
      }));
      this.setState({
        saving: true
      });
      try {
        await kwService("").postAsync(models);
        this.setState({
          saving: false,
          adding: false,
          addTerms: "",
          addTopic: "",
          addTarget: ""
        });
        this.reload();
      } catch (e) {
        this.setState({
          saving: false,
          notice: "Adding keywords didn't work. Check the terms and try again."
        });
      }
    });
    _defineProperty(this, "toggleActive", async k => {
      this.setState({
        busyId: String(k.id)
      });
      try {
        await kwService(`/${k.id}`).putAsync({
          term: k.term,
          engines: k.engines,
          targetUrl: k.targetUrl,
          topicGroup: k.topicGroup,
          active: k.active === false
        });
        this.setState({
          busyId: null
        });
        this.reload();
      } catch (e) {
        this.setState({
          busyId: null,
          notice: "That change didn't save. Try again."
        });
      }
    });
    _defineProperty(this, "removeKeyword", async k => {
      this.setState({
        busyId: String(k.id)
      });
      try {
        await kwService("").deleteAsync(k.id);
        this.setState({
          busyId: null
        });
        this.reload();
      } catch (e) {
        this.setState({
          busyId: null,
          notice: "Delete didn't work. Try again."
        });
      }
    });
    _defineProperty(this, "trackOpportunity", async o => {
      try {
        await kwService("").postAsync([{
          term: o.keyword,
          engines: "google",
          topicGroup: o.seedTerm || null
        }]);
        this.setState({
          notice: `Now tracking "${o.keyword}".`
        });
        this.reload();
      } catch (e) {
        this.setState({
          notice: "Couldn't start tracking that term. Try again."
        });
      }
    });
    _defineProperty(this, "refreshNow", async () => {
      if (this.state.refreshing) return;
      this.setState({
        refreshing: true,
        notice: "Checking where you rank. This can take a minute."
      });
      try {
        await kwService("/refresh").postAsync({});
        this.setState({
          refreshing: false,
          notice: null
        });
        this.reload();
      } catch (e) {
        this.setState({
          refreshing: false,
          notice: "The check is taking longer than usual. Results may still arrive; reload in a minute."
        });
      }
    });
    _defineProperty(this, "addCompetitor", async () => {
      const domain = this.state.compDomain.trim();
      if (!domain) return;
      try {
        await kwService("/competitors").postAsync({
          domain,
          label: null,
          isSelf: false
        });
        this.setState({
          compDomain: ""
        });
        this.reload();
      } catch (e) {
        this.setState({
          notice: "Adding that competitor didn't work. Check the web address."
        });
      }
    });
    _defineProperty(this, "removeCompetitor", async c => {
      try {
        await kwService("/competitors").deleteAsync(c.id);
        this.reload();
      } catch (e) {
        this.setState({
          notice: "Removing that competitor didn't work."
        });
      }
    });
    this.state = {
      data: null,
      error: false,
      filter: null,
      adding: false,
      addTerms: "",
      addTopic: "",
      addTarget: "",
      addEngines: {
        google: true,
        bing: false
      },
      saving: false,
      refreshing: false,
      notice: null,
      compDomain: "",
      busyId: null
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load() {
    const [keywords, overview, sov, competitors, opps, cannibal] = await Promise.all([safe(kwService("").getAsync(), null), safe(kwService("/overview").getAsync(), []), safe(kwService("/share-of-voice").getAsync(), []), safe(kwService("/competitors").getAsync(), null), safe(kwService("/opportunities").getAsync(), []), safe(kwService("/cannibalization").getAsync(), [])]);
    if (!this._alive) return;
    if (keywords === null) {
      this.setState({
        error: true
      });
      return;
    }
    this.setState({
      data: {
        keywords,
        overview,
        sov,
        competitors,
        opps,
        cannibal
      }
    });
  }
  // ---------------------------------------------------------- derived rows

  // One row per keyword: best position across its engines (google shown,
  // bing in its own column when any term tracks it).
  rows() {
    const {
      keywords,
      overview
    } = this.state.data;
    const byKey = {};
    (overview || []).forEach(o => {
      byKey[`${o.keywordId}:${o.engine}`] = o;
    });
    return (keywords || []).map(k => {
      const engines = String(k.engines || "google").split(",").map(x => x.trim()).filter(Boolean);
      const g = byKey[`${k.id}:google`] || null;
      const b = byKey[`${k.id}:bing`] || null;
      const primary = engines.includes("google") ? g : b || g;
      const checked = [g, b].filter(o => o && o.checkedDate).map(o => isoDay(o.checkedDate)).sort().pop() || null;
      const position = primary && primary.currentPosition != null ? Number(primary.currentPosition) : null;
      return {
        k,
        engines,
        g,
        b,
        primary,
        checked,
        position,
        paused: k.active === false,
        volume: k.searchVolume == null ? null : Number(k.searchVolume)
      };
    });
  }

  // ---------------------------------------------------------- renderers

  renderVerdict(active, sovLatest) {
    const tracked = active.length;
    if (!tracked) {
      return /*#__PURE__*/React.createElement(Verdict, {
        tone: "grey",
        title: "You're not tracking any search terms yet"
      }, "Add the words your buyers type into a search engine, then check where you show up.");
    }
    const checked = active.filter(r => r.primary);
    if (!checked.length) {
      return /*#__PURE__*/React.createElement(Verdict, {
        tone: "grey",
        title: `You're tracking ${plural(tracked, "term")}, not checked yet`
      }, "Click Check now to see where you show up for each one.");
    }
    const top100 = active.filter(r => r.position != null).length;
    const top10 = active.filter(r => r.position != null && r.position <= 10).length;
    const gap = active.filter(r => r.volume > 0 && r.position == null).length;
    const comps = sovLatest.rows.filter(r => !r.isSelf && Number(r.keywordsRanked) > 0);
    const bestComp = comps.slice().sort((a, b) => Number(b.keywordsRanked) - Number(a.keywordsRanked))[0];
    const parts = [];
    if (gap) parts.push(`${plural(gap, "term")} people search for ${gap === 1 ? "has" : "have"} no page of yours in the top 100.`);else if (top10 < tracked) parts.push("Every term people search for has a page of yours in the top 100.");
    if (bestComp) parts.push(`${bestComp.domain} ranks for ${Number(bestComp.keywordsRanked)} of your terms.`);else if (!(this.state.data.competitors || []).some(c => !c.isSelf)) parts.push("Add competitors to see how you compare.");
    const green = top10 > tracked / 2;
    const title = green ? `You're in the top 10 for ${top10} of your ${tracked} terms` : `You're in the top 100 for ${top100} of your ${tracked} terms`;
    return /*#__PURE__*/React.createElement(Verdict, {
      tone: green ? "green" : top100 === 0 ? "red" : "amber",
      title: title
    }, parts.join(" "));
  }
  renderRibbon(active, all, sovLatest) {
    const top10 = active.filter(r => r.position != null && r.position <= 10);
    const best = active.filter(r => r.position != null).sort((a, b) => a.position - b.position)[0];
    const volume = active.reduce((s, r) => s + (r.volume || 0), 0);
    const paused = all.length - active.length;
    const tiles = [{
      l: "Terms tracked",
      v: num(active.length),
      s: paused ? `${paused} paused` : ""
    }, {
      l: "In the top 10",
      v: num(top10.length),
      s: best ? `best: ${best.k.term} (#${best.position})` : "none ranking yet"
    }, {
      l: "Searches a month",
      v: num(volume),
      s: "across your terms"
    }];
    const self = sovLatest.rows.find(r => r.isSelf);
    if (self && self.sharePct != null) {
      const top = sovLatest.rows.find(r => !r.isSelf);
      tiles.push({
        l: "Your share of voice",
        v: fmtShare(self.sharePct),
        s: top && top.sharePct != null ? `vs ${top.domain} ${fmtShare(top.sharePct)}` : ""
      });
    }
    return /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: tiles.length === 3 ? 3 : 4,
      tiles: tiles
    });
  }
  renderAddForm() {
    if (!this.state.adding) return null;
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-add"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Add keywords"), /*#__PURE__*/React.createElement("span", null, "One per line, the words a buyer would type into a search engine.")), /*#__PURE__*/React.createElement("textarea", {
      placeholder: "One term per line, for example:\nyour product + your city\nbest [what you sell] for beginners",
      value: this.state.addTerms,
      autoFocus: true,
      onChange: e => this.setState({
        addTerms: e.target.value
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement("input", {
      placeholder: "Group name (optional)",
      value: this.state.addTopic,
      onChange: e => this.setState({
        addTopic: e.target.value
      })
    }), /*#__PURE__*/React.createElement("input", {
      className: "wide",
      placeholder: "Page you want to rank (optional web address)",
      value: this.state.addTarget,
      onChange: e => this.setState({
        addTarget: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, ENGINES.map(e => /*#__PURE__*/React.createElement("label", {
      key: e,
      className: "aps-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!this.state.addEngines[e],
      onChange: ev => this.setState({
        addEngines: {
          ...this.state.addEngines,
          [e]: ev.target.checked
        }
      })
    }), e === "google" ? "Google" : "Bing"))), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: this.state.saving || !this.state.addTerms.trim(),
      onClick: this.addKeywords
    }, this.state.saving ? "Adding…" : "Add"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        adding: false
      })
    }, "Cancel")));
  }
  renderTable(all) {
    const comps = (this.state.data || {}).competitors;
    const noSelf = Array.isArray(comps) && !selfRow(comps);
    const active = all.filter(r => !r.paused);
    const sets = {
      act: active.filter(r => r.volume > 0 && (r.position == null || r.position > 10)).sort((a, b) => (b.volume || 0) - (a.volume || 0)),
      all: all.slice().sort((a, b) => a.paused - b.paused || (b.volume || 0) - (a.volume || 0) || String(a.k.term).localeCompare(String(b.k.term))),
      ranking: active.filter(r => r.position != null).sort((a, b) => a.position - b.position),
      paused: all.filter(r => r.paused)
    };
    const filter = this.state.filter || (sets.act.length ? "act" : "all");
    const rows = sets[filter];
    const showBing = all.some(r => r.engines.includes("bing") && r.engines.includes("google") && r.b);
    const showTopic = all.some(r => r.k.topicGroup);
    const showCpc = all.some(r => r.k.cpc != null);
    const last = all.map(r => r.checked).filter(Boolean).sort().pop();
    const busy = this.state.busyId;
    const empty = {
      act: "Nothing urgent here. Every term people search for already has a page of yours in the top 10.",
      all: "No terms yet. Use Add keywords to start.",
      ranking: "None of your terms are in the top 100 yet.",
      paused: "No paused terms."
    }[filter];
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-terms"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-pills"
    }, FILTERS.map(([v, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: v,
      className: `aps-pill${filter === v ? " on" : ""}`,
      onClick: () => this.setState({
        filter: v
      })
    }, l, " ", /*#__PURE__*/React.createElement("span", null, sets[v].length))), last ? /*#__PURE__*/React.createElement("span", {
      className: "aps-dim aps-push"
    }, "Last checked ", fmtDate(last)) : null), rows.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, empty) : /*#__PURE__*/React.createElement("div", {
      className: "aps-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "aps-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Search term"), showTopic && /*#__PURE__*/React.createElement("th", null, "Group"), /*#__PURE__*/React.createElement("th", null, "Your position"), showBing && /*#__PURE__*/React.createElement("th", null, "Bing"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Searches a month"), showCpc && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Cost per click"), /*#__PURE__*/React.createElement("th", null, "Next step"), /*#__PURE__*/React.createElement("th", {
      className: "act"
    }))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => {
      const {
        k
      } = r;
      let step = "";
      if (!r.paused && r.primary && !noSelf) {
        if (r.position == null) step = "Write a page for it";else if (r.position > 10) step = "Improve your page";
      }
      return /*#__PURE__*/React.createElement("tr", {
        key: String(k.id),
        className: r.paused ? "paused" : ""
      }, /*#__PURE__*/React.createElement("td", {
        className: "term"
      }, /*#__PURE__*/React.createElement(Link, {
        to: `/Keywords/${k.id}`
      }, k.term)), showTopic && /*#__PURE__*/React.createElement("td", {
        className: "aps-dim"
      }, k.topicGroup || ""), /*#__PURE__*/React.createElement("td", null, r.paused ? /*#__PURE__*/React.createElement(Chip, {
        tone: "grey"
      }, "Paused") : !r.primary ? /*#__PURE__*/React.createElement("span", {
        className: "aps-dim"
      }, "Not checked yet") : noSelf ? /*#__PURE__*/React.createElement("span", {
        className: "aps-dim",
        title: "Tell us which website is yours to see where it ranks"
      }, "Your site isn't set") : /*#__PURE__*/React.createElement(PositionChip, {
        position: r.position,
        previous: r.primary.previousPosition,
        url: r.primary.rankedUrl
      })), showBing && /*#__PURE__*/React.createElement("td", null, r.engines.includes("bing") && r.b && !r.paused ? /*#__PURE__*/React.createElement(PositionChip, {
        position: r.b.currentPosition,
        previous: r.b.previousPosition,
        url: r.b.rankedUrl
      }) : null), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, r.volume == null ? /*#__PURE__*/React.createElement("span", {
        className: "aps-dim"
      }, "-") : num(r.volume)), showCpc && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, k.cpc == null ? "" : `$${Number(k.cpc).toFixed(2)}`), /*#__PURE__*/React.createElement("td", null, step), /*#__PURE__*/React.createElement("td", {
        className: "act"
      }, /*#__PURE__*/React.createElement(RowMenu, {
        disabled: busy === String(k.id),
        items: [{
          label: r.paused ? "Resume" : "Pause",
          onClick: () => this.toggleActive(k)
        }, {
          label: "Delete",
          confirm: "Confirm delete",
          danger: true,
          onClick: () => this.removeKeyword(k)
        }]
      })));
    })))));
  }
  renderShareOfVoice(sovLatest, competitors) {
    const others = (competitors || []).filter(c => !c.isSelf);
    const max = Math.max(1, ...sovLatest.rows.map(r => Number(r.sharePct) || 0));
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-sov"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Share of voice"), /*#__PURE__*/React.createElement("span", null, sovLatest.rows.length ? `Share of the search clicks on your terms each site is estimated to get${sovLatest.date ? `, as of ${fmtDate(sovLatest.date)}` : ""}.` : "How often you show up across your terms compared with competitors.")), sovLatest.rows.length > 0 ? /*#__PURE__*/React.createElement("div", {
      className: "aps-bars"
    }, sovLatest.rows.slice(0, 8).map((r, i) => {
      const pct = Number(r.sharePct) || 0;
      return /*#__PURE__*/React.createElement("div", {
        key: i,
        className: `aps-bar-row${r.isSelf ? " self" : ""}`,
        title: r.keywordsRanked != null ? `Ranks for ${r.keywordsRanked} of your terms` : undefined
      }, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, r.domain, r.isSelf ? " (you)" : ""), /*#__PURE__*/React.createElement("span", {
        className: "aps-bar"
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          width: `${Math.max(2, pct / max * 100)}%`
        }
      })), /*#__PURE__*/React.createElement("span", {
        className: "pct"
      }, fmtShare(pct)));
    })) : others.length ? /*#__PURE__*/React.createElement("div", {
      className: "aps-dim aps-note"
    }, "Your competitors are saved. Their share shows up after the next check.") : null, /*#__PURE__*/React.createElement("div", {
      className: "aps-comp"
    }, /*#__PURE__*/React.createElement("b", {
      className: "aps-sub"
    }, others.length ? "Competitors" : "Add competitors to compare"), others.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "aps-tags"
    }, others.map(c => /*#__PURE__*/React.createElement("span", {
      key: String(c.id),
      className: "aps-tag"
    }, c.domain, /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-label": `Remove ${c.domain}`,
      title: "Remove",
      onClick: () => this.removeCompetitor(c)
    }, "\xD7")))), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement("input", {
      className: "wide",
      placeholder: "competitor.com",
      value: this.state.compDomain,
      onChange: e => this.setState({
        compDomain: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.addCompetitor();
      }
    }), /*#__PURE__*/React.createElement(Button, {
      disabled: !this.state.compDomain.trim(),
      onClick: this.addCompetitor
    }, "Add"))));
  }
  renderCannibalization(rows) {
    if (!rows || rows.length === 0) return null;
    const short = u => (u || "").replace(/^https?:\/\//, "");
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Pages getting in each other's way"), /*#__PURE__*/React.createElement("span", null, "For these terms a different page of yours shows up than the one you picked, or several of your pages split the spot.")), /*#__PURE__*/React.createElement("div", {
      className: "aps-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "aps-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Search term"), /*#__PURE__*/React.createElement("th", null, "What's happening"), /*#__PURE__*/React.createElement("th", null, "Position"), /*#__PURE__*/React.createElement("th", null, "Page that shows up"), /*#__PURE__*/React.createElement("th", null, "Page you picked"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((r, i) => /*#__PURE__*/React.createElement("tr", {
      key: i
    }, /*#__PURE__*/React.createElement("td", {
      className: "term"
    }, /*#__PURE__*/React.createElement(Link, {
      to: `/Keywords/${r.keywordId}`
    }, r.term)), /*#__PURE__*/React.createElement("td", null, r.conflictType === "multi_url" ? "Several of your pages" : "A different page"), /*#__PURE__*/React.createElement("td", null, r.position == null ? "" : /*#__PURE__*/React.createElement(PositionChip, {
      position: r.position
    })), /*#__PURE__*/React.createElement("td", {
      className: "url",
      title: r.rankedUrl
    }, short(r.rankedUrl)), /*#__PURE__*/React.createElement("td", {
      className: "url",
      title: r.targetUrl
    }, short(r.targetUrl))))))));
  }
  renderOpportunities(opps, keywords) {
    if (!opps || opps.length === 0) return null;
    const tracked = new Set(keywords.map(k => String(k.term || "").toLowerCase()));
    const showCpc = opps.some(o => o.cpc != null);
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Terms worth tracking"), /*#__PURE__*/React.createElement("span", null, "Related searches people make that you aren't tracking yet.")), /*#__PURE__*/React.createElement("div", {
      className: "aps-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "aps-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Search term"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Searches a month"), showCpc && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Cost per click"), /*#__PURE__*/React.createElement("th", {
      className: "act"
    }))), /*#__PURE__*/React.createElement("tbody", null, opps.slice(0, 15).map((o, i) => /*#__PURE__*/React.createElement("tr", {
      key: i
    }, /*#__PURE__*/React.createElement("td", {
      className: "term plain"
    }, o.keyword), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, o.searchVolume == null ? "" : num(o.searchVolume)), showCpc && /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, o.cpc == null ? "" : `$${Number(o.cpc).toFixed(2)}`), /*#__PURE__*/React.createElement("td", {
      className: "act"
    }, tracked.has(String(o.keyword || "").toLowerCase()) ? /*#__PURE__*/React.createElement("span", {
      className: "aps-dim"
    }, "Tracking") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "aps-link",
      onClick: () => this.trackOpportunity(o)
    }, "Track"))))))));
  }
  render() {
    const {
      data,
      error,
      notice,
      refreshing
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const all = this.rows();
    const active = all.filter(r => !r.paused);
    const sovLatest = latestSov(data.sov);
    // competitors null = the call failed: don't ask for the site on a guess.
    const noSelf = Array.isArray(data.competitors) && !selfRow(data.competitors);
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view keywords-view wa-view aps-root"
    }, /*#__PURE__*/React.createElement(BackToApps, {
      title: "Keyword Intelligence",
      crumb: "Keyword Intelligence",
      subtitle: "Where you show up in search for the terms that matter, against your competitors",
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
        onClick: () => this.setState({
          adding: !this.state.adding
        })
      }, "Add keywords"), /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: refreshing || !active.length,
        onClick: this.refreshNow
      }, refreshing ? "Checking…" : "Check now"))
    }), notice && /*#__PURE__*/React.createElement("div", {
      className: "aps-notice"
    }, notice, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "aps-link",
      onClick: () => this.setState({
        notice: null
      })
    }, "Dismiss")), this.renderAddForm(), noSelf && active.length > 0 ? /*#__PURE__*/React.createElement(SelfSiteCard, {
      competitors: data.competitors,
      what: "Where you rank is measured on your own site. Until we know which one it is, every term reads as not in the top 100.",
      onSaved: () => {
        this.reload();
        this.setState({
          notice: "Saved. Click Check now to see where your site ranks."
        });
      }
    }) : this.renderVerdict(active, sovLatest), active.length > 0 && !noSelf && this.renderRibbon(active, all, sovLatest), /*#__PURE__*/React.createElement("div", {
      className: "aps-split"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-main"
    }, this.renderTable(all)), /*#__PURE__*/React.createElement("div", {
      className: "aps-side"
    }, this.renderShareOfVoice(sovLatest, data.competitors))), this.renderCannibalization(data.cannibal), this.renderOpportunities(data.opps, data.keywords));
  }
}
export default withRouter(KeywordsViewComponent);